import { useTranslation } from "react-i18next";
import { BaseTestQuestionDto } from "@packages/api/madlenAPI.schemas";
import { X, Plus } from "lucide-react";
import { Button } from "../../../../../core";

interface TestQuestionEditorProps {
  question: BaseTestQuestionDto;
  onQuestionChange: (question: BaseTestQuestionDto) => void;
  gradeLevel: number | undefined;
}

export const TestQuestionEditor = ({
  question,
  onQuestionChange,
  gradeLevel,
}: TestQuestionEditorProps) => {
  const { t } = useTranslation();

  const maxOptions = gradeLevel ? (gradeLevel >= 9 ? 5 : 4) : 5;
  const currentOptionsCount = Object.keys(question?.options || {})?.length;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
        {t("assignments.generator.preview.options")}
      </label>
      {Object.entries(question.options).map(([key, option], index) => (
        <div key={index} className="flex items-center mb-2">
          <Button
            onClick={() => {
              onQuestionChange({
                ...question,
                answer: key,
              });
            }}
            size="xl"
            bgColor={
              question.answer === key
                ? "bg-green-500 hover:bg-green-600"
                : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
            }
            textColor={
              question.answer === key
                ? "text-white"
                : "text-gray-700 dark:text-gray-200"
            }
            variant="outline"
            className={`border rounded-l-md ${
              question.answer === key
                ? "border-green-600"
                : "border-gray-200 dark:border-gray-600"
            } border-r-0`}
          >
            {key}
          </Button>
          <textarea
            rows={2}
            value={option}
            onChange={(e) => {
              const newOptions = { ...question.options };
              newOptions[key] = e.target.value;
              onQuestionChange({
                ...question,
                options: newOptions,
              });
            }}
            className="flex-1 p-2 rounded-r-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100 border max-h-16 resize-none"
            placeholder={t(`assignments.generator.preview.option`, {
              option: key,
            })}
          />
          <Button
            onClick={() => {
              const oldOptions = { ...question.options };
              const newOptions: Record<string, string> = {};

              // Get all keys and values, excluding the one to delete
              const entries = Object.entries(oldOptions).filter(
                ([k]) => k !== key,
              );

              // Reassign keys in sequence
              entries.forEach(([, value], index) => {
                newOptions[String.fromCharCode(65 + index)] = value;
              });

              let newAnswer = question.answer;
              if (question.answer === key) {
                // If answer was the removed option, set it to empty string
                newAnswer = "";
              } else if (question.answer > key) {
                // If answer was after the removed option, shift it one letter back
                const answerIndex = question?.answer?.charCodeAt(0) - 65 - 1;
                if (answerIndex >= 0 && answerIndex < entries.length) {
                  newAnswer = String.fromCharCode(65 + answerIndex);
                }
              }

              onQuestionChange({
                ...question,
                options: newOptions,
                answer: newAnswer,
              });
            }}
            size="xs"
            textColor="text-gray-500 hover:text-red-500"
            className="ml-2"
            title="Remove option"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      {currentOptionsCount < maxOptions && (
        <Button
          onClick={() => {
            const newOptions = { ...question.options };
            newOptions[
              String.fromCharCode(65 + Object.keys(newOptions).length)
            ] = "";
            onQuestionChange({
              ...question,
              options: newOptions,
            });
          }}
          size="sm"
          textColor="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          {t("assignments.generator.preview.addOption")}
        </Button>
      )}
    </div>
  );
};
